/*!
 * The look (spec) of a pinball SigmaBot.
 *
 * The name lists come from sigmashake-vcs src/lib/bot-character.ts (The Unlicense, public domain).
 * That file sits in the same family as the renderer files that this folder ports from
 * ClaudeAnimationBase - https://github.com/JohnHeibel/ClaudeAnimationBase
 * MIT License. Copyright (c) 2026 John Heibel. The full MIT license text is in NOTICE.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */
// SPDX-License-Identifier: GPL-3.0-or-later
// Changes from upstream: only the six fields that the table draws (body, palette, eyes, mouth,
// hat, accessory). The lists have the same names in the same order. Each field is a name from a
// list. No field holds free text. An unknown name falls back to the first name of its list,
// as in the upstream renderer.

export const SIGMABOT_BODIES = [
  "blob",
  "bean",
  "box",
  "tall",
  "tiny",
  "round",
  "pear",
  "bunny",
  "cat",
  "robot",
] as const;
export const SIGMABOT_PALETTES = [
  "ember",
  "ocean",
  "moss",
  "grape",
  "sun",
  "rose",
  "slate",
  "mint",
  "coral",
  "night",
  "peach",
  "sky",
  "lime",
  "snow",
  "gold",
  "cherry",
  "cocoa",
  "coal",
] as const;
export const SIGMABOT_EYES = [
  "dot",
  "wide",
  "sleepy",
  "spark",
  "wink",
  "angry",
  "heart",
  "x",
  "happy",
  "sad",
  "surprised",
  "dizzy",
  "star",
  "lashes",
  "smug",
  "side",
] as const;
export const SIGMABOT_MOUTHS = [
  "smile",
  "grin",
  "flat",
  "open",
  "tongue",
  "fang",
  "wavy",
  "frown",
  "smirk",
  "oh",
  "kiss",
  "teeth",
  "cat",
  "pout",
] as const;
export const SIGMABOT_HATS = [
  "none",
  "cap",
  "crown",
  "party",
  "wizard",
  "beanie",
  "tophat",
  "halo",
  "headband",
  "bow",
  "pirate",
  "chef",
  "cowboy",
  "flower",
  "antlers",
  "helmet",
] as const;
export const SIGMABOT_ACCESSORIES = [
  "none",
  "glasses",
  "shades",
  "scarf",
  "bowtie",
  "antenna",
  "cape",
  "backpack",
  "necklace",
  "earrings",
  "mask",
  "monocle",
  "headphones",
  "badge",
] as const;

export type SigmaBotBody = (typeof SIGMABOT_BODIES)[number];
export type SigmaBotPalette = (typeof SIGMABOT_PALETTES)[number];
export type SigmaBotEyes = (typeof SIGMABOT_EYES)[number];
export type SigmaBotMouth = (typeof SIGMABOT_MOUTHS)[number];
export type SigmaBotHat = (typeof SIGMABOT_HATS)[number];
export type SigmaBotAccessory = (typeof SIGMABOT_ACCESSORIES)[number];

/** The look of one SigmaBot. The same spec always gives the same mesh. */
export interface SigmaBotSpec {
  readonly body: SigmaBotBody;
  readonly palette: SigmaBotPalette;
  readonly eyes: SigmaBotEyes;
  readonly mouth: SigmaBotMouth;
  readonly hat: SigmaBotHat;
  readonly accessory: SigmaBotAccessory;
}

/** A name that may be unknown, for example from old data. */
function pick<T extends string>(list: readonly T[], value: unknown): T {
  return list.includes(value as T) ? (value as T) : (list[0] as T);
}

/** Make a complete spec. An unknown or missing field gets the first name of its list. */
export function normalizeSpec(spec: Partial<Record<keyof SigmaBotSpec, unknown>>): SigmaBotSpec {
  return {
    body: pick(SIGMABOT_BODIES, spec.body),
    palette: pick(SIGMABOT_PALETTES, spec.palette),
    eyes: pick(SIGMABOT_EYES, spec.eyes),
    mouth: pick(SIGMABOT_MOUTHS, spec.mouth),
    hat: pick(SIGMABOT_HATS, spec.hat),
    accessory: pick(SIGMABOT_ACCESSORIES, spec.accessory),
  };
}
